// Per spec section 3.7 + 3.8 + 7.12: manual retry orchestration ONLY.
//
// Silent retry lives in lib/transport.ts's withSilentRetry (Task 14). Gate 6
// verified that @ai-sdk/react@3.0.170's regenerate() does NOT support a reuseId
// flag, so silent retry must happen below useChat at the fetch boundary —
// withSilentRetry re-POSTs the identical request body so the message_id is
// preserved for backend idempotency dedup.
//
// This hook handles the manual retry path: when useChat surfaces a persistent
// error (silent retry exhausted at the transport layer, OR a 5xx came back, OR
// Bedrock emitted an error frame), we surface RetryBubble; clicking it calls
// regenerate() for a FRESH message_id (no options — SDK generates by default).

import { useChat } from '@ai-sdk/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { makeChatTransport } from '../lib/transport'
import { resolveCoalesceWindow } from './useCoalesce'

export interface UseMiklaChatOptions {
  apiBase: string
  sessionId: string
  vendorId: string
  coalesceWindowMs: number | null
  fetchImpl?: typeof fetch
}

export function useMiklaChat(opts: UseMiklaChatOptions) {
  const [showRetryBubble, setShowRetryBubble] = useState(false)

  // Memoize transport so its identity is stable across renders. useChat's
  // transport is read on mount; identity churn would be wasteful even if it
  // didn't reset internal state.
  const transport = useMemo(
    () => makeChatTransport({
      apiBase: opts.apiBase,
      sessionId: opts.sessionId,
      fetchImpl: opts.fetchImpl,
    }),
    [opts.apiBase, opts.sessionId, opts.fetchImpl],
  )

  const chat = useChat({
    transport,
    onError: () => {
      // No silent retry logic here — that's the transport's job. By the time
      // useChat reports an error, withSilentRetry has either already retried
      // (TypeError) and failed, OR the error is non-retryable (HTTP 5xx,
      // Bedrock error frame).
      setShowRetryBubble(true)
    },
  })

  // Hide bubble when status returns to 'ready' without error. This covers the
  // post-manualRetry success path: regenerate() flips status to 'submitted',
  // then 'streaming', then 'ready' on success — at which point we drop the
  // stale bubble.
  useEffect(() => {
    if (chat.status === 'ready' && !chat.error) {
      setShowRetryBubble(false)
    }
  }, [chat.status, chat.error])

  const coalesceWindow = resolveCoalesceWindow(opts.coalesceWindowMs)

  const sendCoalesced = useCallback(
    (text: string) => {
      chat.sendMessage({ text })
    },
    [chat],
  )

  const manualRetry = useCallback(() => {
    setShowRetryBubble(false)
    // No options — SDK generates a fresh message_id by default. This is the
    // distinction from silent retry (which preserves the id for idempotency).
    chat.regenerate()
  }, [chat])

  return {
    messages: chat.messages,
    setMessages: chat.setMessages,
    status: chat.status,
    error: chat.error,
    showRetryBubble,
    coalesceWindow,
    sendCoalesced,
    stop: chat.stop,
    manualRetry,
  }
}
